import React from 'react';
import CloseRounded from '@mui/icons-material/CloseRounded';
import GlobalStyles from '@mui/material/GlobalStyles';
import { Box, IconButton, Modal as MuiModal, Typography } from '@mui/material';

import { modalRootCenteredSx, safeAreaInset } from '../platform/safeArea';

/** Google Places appends `.pac-container` to `body`. Modal scroll-lock + focus trap break it — fix z-index and opt out when using Places. */
const placesAutocompleteGlobalStyles = (
  <GlobalStyles
    styles={{
      '.pac-container': {
        zIndex: '2147483647 !important',
      },
    }}
  />
);

/**
 * @param {{ bleed?: boolean }} props.bleed - Skip the title/close header row and all padding so
 * content (e.g. an edge-to-edge map) can fill the modal. Works for both `fullScreen` and centred
 * dialogs. When bleed + centred, corners stay rounded (`borderRadius: 2`) and overflow is hidden
 * so the map clips to the dialog radius. Content is responsible for its own safe-area padding
 * and for providing a way back (e.g. a back / close button).
 * @param {{ maxWidth?: number }} props.maxWidth - Caps the width of a centred dialog. Ignored when
 * `fullScreen`.
 * @param {{ height?: string | number }} props.height - Centred dialogs only: gives the dialog a
 * definite height so `height: 100%` children (e.g. a flex-column map + bottom sheet) can resolve.
 * Without it a bleed dialog collapses and its content is clipped.
 */
export default function Modal({
  open,
  onClose,
  fullScreen,
  bleed,
  children,
  title,
  maxWidth = 440,
  height,
}) {
  const showHeader = !bleed;
  return (
    <>
      {open ? placesAutocompleteGlobalStyles : null}
      <MuiModal
        open={open}
        onClose={onClose}
        sx={fullScreen ? undefined : modalRootCenteredSx}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 24,
            outline: 0,
            overflow: bleed ? 'hidden' : 'auto',
            boxSizing: 'border-box',
            ...(fullScreen
              ? {
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  borderRadius: 0,
                  my: 0,
                  mx: 0,
                  ...(bleed
                    ? { p: 0 }
                    : {
                        pt: `calc(12px + ${safeAreaInset.top})`,
                        pb: `calc(16px + ${safeAreaInset.bottom})`,
                        pl: `calc(16px + ${safeAreaInset.left})`,
                        pr: `calc(16px + ${safeAreaInset.right})`,
                      }),
                }
              : {
                  position: 'relative',
                  width: '100%',
                  maxWidth,
                  maxHeight: '100%',
                  borderRadius: 2,
                  my: 'auto',
                  mx: 'auto',
                  ...(height ? { height } : null),
                  ...(bleed
                    ? { p: 0 }
                    : {
                        p: 2,
                        pt: 1.5,
                      }),
                }),
          }}
        >
          {bleed && !fullScreen && onClose ? (
            <IconButton
              aria-label="Close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                // Matches the 44px-tall search input and its `20px + safe-area` top offset in
                // AddAddressForm, so the close button sits on the same centre line.
                top: `calc(20px + ${safeAreaInset.top})`,
                right: 12,
                width: 44,
                height: 44,
                zIndex: 4,
                bgcolor: '#fff',
                boxShadow: 2,
                color: 'text.secondary',
                '&:hover': { bgcolor: '#fafafa' },
              }}
            >
              <CloseRounded />
            </IconButton>
          ) : null}
          {showHeader ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.5,
              }}
            >
              {title ? (
                <Typography variant="h6" component="h2" sx={{ pr: 1, lineHeight: 1.3, flex: 1 }}>
                  {title}
                </Typography>
              ) : (
                <Box sx={{ flex: 1 }} />
              )}
              {onClose ? (
                <IconButton
                  aria-label="Close"
                  onClick={onClose}
                  size="small"
                  sx={{ mt: -0.5, mr: -0.5, color: 'text.secondary' }}
                >
                  <CloseRounded />
                </IconButton>
              ) : null}
            </Box>
          ) : null}
          {children}
        </Box>
      </MuiModal>
    </>
  );
}
